<template>
  <BaseModal
    :is-visible="isVisible"
    title="Edit Employee"
    subtitle="Update employee information"
    :icon="UserPen"
    size="2xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
              <div class="space-y-6">
                <form @submit.prevent="handleSubmit" class="space-y-6">
                  <!-- Personal Information Section -->
                  <div class="space-y-4">
                    <h4
                      class="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2"
                    >
                      Personal Information
                    </h4>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- First Name -->
                      <div class="form-group">
                        <label
                          for="first_name"
                          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                        >
                          First Name <span class="text-red-500">*</span>
                        </label>
                        <input
                          id="first_name"
                          v-model="formData.first_name"
                          type="text"
                          class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          :class="{
                            'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                              errors.first_name
                          }"
                          placeholder="Enter first name"
                          required
                        />
                        <p v-if="errors.first_name" class="mt-2 text-sm text-red-600 font-medium">
                          {{ errors.first_name }}
                        </p>
                      </div>

                      <!-- Last Name -->
                      <div class="form-group">
                        <label
                          for="last_name"
                          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                        >
                          Last Name <span class="text-red-500">*</span>
                        </label>
                        <input
                          id="last_name"
                          v-model="formData.last_name"
                          type="text"
                          class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          :class="{
                            'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                              errors.last_name
                          }"
                          placeholder="Enter last name"
                          required
                        />
                        <p v-if="errors.last_name" class="mt-2 text-sm text-red-600 font-medium">
                          {{ errors.last_name }}
                        </p>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- Middle Name -->
                      <div class="form-group">
                        <label
                          for="middle_name"
                          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                        >
                          Middle Name
                        </label>
                        <input
                          id="middle_name"
                          v-model="formData.middle_name"
                          type="text"
                          class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          :class="{
                            'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                              errors.middle_name
                          }"
                          placeholder="Enter middle name (optional)"
                        />
                        <p v-if="errors.middle_name" class="mt-2 text-sm text-red-600 font-medium">
                          {{ errors.middle_name }}
                        </p>
                      </div>

                      <!-- Email -->
                      <div class="form-group">
                        <label
                          for="email"
                          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                        >
                          Email Address <span class="text-red-500">*</span>
                        </label>
                        <input
                          id="email"
                          v-model="formData.email"
                          type="email"
                          class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          :class="{
                            'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                              errors.email || isEmailInvalid
                          }"
                          placeholder="Enter email address"
                          required
                        />
                        <p v-if="errors.email" class="mt-2 text-sm text-red-600 font-medium">
                          {{ errors.email }}
                        </p>
                      </div>
                    </div>

                    <!-- Contact Information -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- Phone Number -->
                      <div class="form-group">
                        <label
                          for="phone_number"
                          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                        >
                          Phone Number
                        </label>
                        <input
                          id="phone_number"
                          v-model="formData.phone_number"
                          type="tel"
                          class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          :class="{
                            'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                              errors.phone_number
                          }"
                          placeholder="Enter phone number (optional)"
                        />
                        <p v-if="errors.phone_number" class="mt-2 text-sm text-red-600 font-medium">
                          {{ errors.phone_number }}
                        </p>
                      </div>

                      <!-- City -->
                      <div class="form-group">
                        <label
                          for="city"
                          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                        >
                          City
                        </label>
                        <input
                          id="city"
                          v-model="formData.city"
                          type="text"
                          class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          :class="{
                            'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                              errors.city
                          }"
                          placeholder="Enter city (optional)"
                        />
                        <p v-if="errors.city" class="mt-2 text-sm text-red-600 font-medium">
                          {{ errors.city }}
                        </p>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <!-- State -->
                      <div class="form-group">
                        <label
                          for="state"
                          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                        >
                          State
                        </label>
                        <input
                          id="state"
                          v-model="formData.state"
                          type="text"
                          class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          :class="{
                            'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                              errors.state
                          }"
                          placeholder="Enter state (optional)"
                        />
                        <p v-if="errors.state" class="mt-2 text-sm text-red-600 font-medium">
                          {{ errors.state }}
                        </p>
                      </div>

                      <!-- Zip Code -->
                      <div class="form-group">
                        <label
                          for="zip_code"
                          class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2"
                        >
                          Zip Code
                        </label>
                        <input
                          id="zip_code"
                          v-model="formData.zip_code"
                          type="text"
                          class="block w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                          :class="{
                            'border-red-500 focus:border-red-500 focus:ring-red-500/20':
                              errors.zip_code
                          }"
                          placeholder="Enter zip code (optional)"
                        />
                        <p v-if="errors.zip_code" class="mt-2 text-sm text-red-600 font-medium">
                          {{ errors.zip_code }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Password Update Section -->
                  <div class="space-y-4">
                    <div
                      class="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 dark:border-gray-700 pb-2 gap-3"
                    >
                      <h4 class="text-lg font-medium text-gray-900 dark:text-white">
                        Password Update (Optional)
                      </h4>
                      <label class="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" v-model="updatePassword" class="sr-only peer" />
                        <div
                          class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
                        ></div>
                        <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300"
                          >Update Password</span
                        >
                      </label>
                    </div>

                    <div v-if="updatePassword" class="space-y-4">
                      <!-- Password Section -->
                      <div class="form-group">
                        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          New Password <span class="text-red-500">*</span>
                        </label>
                        
                        <div class="relative">
                          <input
                            id="password"
                            v-model="formData.password"
                            :type="showPassword ? 'text' : 'password'"
                            class="block w-full px-4 py-3 pr-10 text-base border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                            :class="{ 'border-red-500 focus:border-red-500 focus:ring-red-500/20': errors.password }"
                            placeholder="Enter new password (minimum 6 characters)"
                            minlength="6"
                          />
                          <button
                            type="button"
                            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            @click="showPassword = !showPassword"
                          >
                            <svg v-if="!showPassword" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          </button>
                        </div>

                        <p v-if="errors.password" class="mt-2 text-sm text-red-600 font-medium">
                          {{ errors.password }}
                        </p>
                        
                        <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          Password must be at least 6 characters long
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Live validation messages -->
                  <div class="mt-3 space-y-2">
                    <!-- Password too short warning -->
                    <div
                      v-if="isPasswordTooShort"
                      class="flex items-center text-sm font-bold text-amber-600 dark:text-amber-400"
                    >
                      <AlertTriangle class="w-4 h-4 mr-2" />
                      Password is too short ({{ formData.password.length }}/6 characters)
                    </div>

                    <!-- Password mismatch warning -->
                    <div
                      v-if="isPasswordMismatch"
                      class="flex items-center text-sm font-bold text-red-600 dark:text-red-400"
                    >
                      <XCircle class="w-4 h-4 mr-2" />
                      Passwords do not match
                    </div>

                    <!-- Name validation message -->
                    <div
                      v-if="nameValidationMessage"
                      class="flex items-center text-sm text-red-600 dark:text-red-400 font-medium"
                    >
                      <XCircle class="w-4 h-4 mr-2" />
                      {{ nameValidationMessage }}
                    </div>

                    <!-- Email validation messages -->
                    <div
                      v-if="isEmailEmpty"
                      class="flex items-center text-sm text-red-600 dark:text-red-400 font-medium"
                    >
                      <XCircle class="w-4 h-4 mr-2" />
                      Email address is required
                    </div>
                    <div
                      v-else-if="isEmailInvalid"
                      class="flex items-center text-sm text-red-600 dark:text-red-400 font-medium"
                    >
                      <XCircle class="w-4 h-4 mr-2" />
                      Please enter a valid email address
                    </div>

                    <!-- All fields valid message -->
                    <div
                      v-if="
                        !nameValidationMessage &&
                        !isEmailEmpty &&
                        !isEmailInvalid &&
                        (!updatePassword || (isPasswordValidLength && !isPasswordMismatch))
                      "
                      class="flex items-center text-sm text-green-600 dark:text-green-400 font-medium"
                    >
                      <CheckCircle class="w-4 h-4 mr-2" />
                      All fields are valid
                    </div>
                  </div>
                </form>

                <!-- Month Assignment Section -->
                <div class="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
                  <div class="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                    <h4 class="text-lg font-medium text-gray-900 dark:text-white">
                      Monthly Module Access
                    </h4>
                    <span v-if="isLoadingAssignments" class="inline-flex items-center text-xs text-blue-600 dark:text-blue-400">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading access...
                    </span>
                  </div>

                  <!-- Loading state -->
                  <div
                    v-if="isLoadingAssignments"
                    class="flex items-center justify-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg border dark:border-gray-700 text-gray-500 dark:text-gray-400"
                  >
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="text-sm font-medium">Fetching current monthly access...</span>
                  </div>

                  <!-- Content when loaded -->
                  <div v-else class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4 border dark:border-gray-700">
                    <p class="text-sm text-gray-600 dark:text-gray-300 mb-3">
                      Set monthly module access for this employee. These will be updated when you save the employee details.
                    </p>

                    <!-- Warning for unsaved changes -->
                    <div
                      v-if="hasMonthAccessChanges"
                      class="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-4"
                    >
                      <div class="flex items-start">
                        <AlertTriangle class="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h4 class="text-sm font-semibold text-amber-800 dark:text-amber-200">
                            Unsaved Changes
                          </h4>
                          <p class="text-sm text-amber-700 dark:text-amber-300 mt-1">
                            You have made changes to the monthly access. Please click the <span class="font-semibold">"Update Employee"</span> button at the bottom to save these changes.
                          </p>
                        </div>
                      </div>
                    </div>

                    <!-- Current Assignments in Form Display -->
                    <div v-if="formData.month_assignments && formData.month_assignments.length > 0" class="mb-4">
                      <h4 class="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Current Access:</h4>
                      <div class="space-y-2">
                        <div
                          v-for="(assignment, index) in formData.month_assignments"
                          :key="index"
                          class="flex items-center justify-between bg-white dark:bg-gray-700 p-3 rounded border dark:border-gray-600"
                        >
                          <div>
                            <span class="font-medium text-gray-900 dark:text-gray-100">{{ assignment.assigned_year }}</span>
                            <span class="text-gray-600 dark:text-gray-300 ml-2">
                              - {{ getMonthNames(assignment.assigned_months).join(', ') }}
                            </span>
                          </div>
                          <div class="flex space-x-2">
                            <button
                              type="button"
                              @click="editAssignmentInForm(index)"
                              class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              @click="removeAssignmentFromForm(index)"
                              class="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="mb-4">
                      <p class="text-sm text-gray-500 dark:text-gray-400 italic bg-white dark:bg-gray-700/50 p-3 rounded border border-dashed border-gray-300 dark:border-gray-600">
                        No monthly module access assigned yet.
                      </p>
                    </div>

                    <!-- Assignment Form -->
                    <div class="border-t dark:border-gray-600 pt-4">
                      <h4 class="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                        {{ editingAssignmentIndex !== null ? 'Edit Access' : 'Add Access' }}
                      </h4>
                      
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <!-- Year Selection -->
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                            Year <span class="text-red-500">*</span>
                          </label>
                          <select
                            v-model="tempAssignmentForm.assigned_year"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            :class="{ 'border-red-500': assignmentErrors.assigned_year }"
                          >
                            <option value="">Select Year</option>
                            <option
                              v-for="year in availableYears"
                              :key="year"
                              :value="year"
                            >
                              {{ year }}
                            </option>
                          </select>
                          <p v-if="assignmentErrors.assigned_year" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ assignmentErrors.assigned_year }}
                          </p>
                        </div>
                      </div>

                      <!-- Month Selection Grid -->
                      <div class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Select Months <span class="text-red-500">*</span>
                        </label>
                        <div class="grid grid-cols-3 md:grid-cols-4 gap-2">
                          <button
                            v-for="month in months"
                            :key="month.value"
                            type="button"
                            @click="toggleMonthInTemp(month.value)"
                            :class="[
                              'px-3 py-2 text-sm rounded-md border transition-colors',
                              tempAssignmentForm.assigned_months.includes(month.value)
                                ? 'bg-blue-600 text-white border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                            ]"
                          >
                            {{ month.label }}
                          </button>
                        </div>
                        <p v-if="assignmentErrors.assigned_months" class="mt-1 text-sm text-red-600 dark:text-red-400">
                          {{ assignmentErrors.assigned_months }}
                        </p>
                      </div>

                      <!-- Assignment Action Buttons -->
                      <div class="flex justify-end space-x-2">
                        <button
                          v-if="editingAssignmentIndex !== null"
                          type="button"
                          @click="cancelEditAssignment"
                          class="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          @click="addOrUpdateAssignmentInForm"
                          class="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:ring-offset-gray-800"
                        >
                          {{ editingAssignmentIndex !== null ? 'Update Access' : 'Add Access' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

    <template #footer>
      <BaseButton
        text="Cancel"
        variant="secondary"
        @click="closeModal"
        :disabled="isSubmitting"
      />
      <BaseButton
        text="Update Employee"
        variant="primary"
        :loading="isSubmitting"
        loading-text="Updating..."
        :disabled="isSubmitDisabled"
        @click="handleSubmit"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue'
import type { Employee } from '@/types/employee'
import { useToastService } from '@/services/toast'
import EmployeeAssignmentService, { 
  type CreateAssignmentPayload
} from '@/services/employee-assignment'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import {
  UserPen,
  AlertTriangle,
  XCircle,
  CheckCircle
} from 'lucide-vue-next'

interface EditEmployeeFormData {
  first_name: string
  last_name: string
  middle_name: string
  email: string
  phone_number: string
  city: string
  state: string
  zip_code: string
  password: string
  password_confirmation: string
  month_assignments?: Array<{
    assigned_year: number
    assigned_months: number[]
  }>
}

// Props
const props = defineProps<{
  isVisible: boolean
  employee: Employee | null
}>()

// Emits
const emit = defineEmits<{
  close: []
  success: [employee: Employee]
}>()

// Toast service
const toast = useToastService()

// Form state
const formData = reactive<EditEmployeeFormData>({
  first_name: '',
  last_name: '',
  middle_name: '',
  email: '',
  phone_number: '',
  city: '',
  state: '',
  zip_code: '',
  password: '',
  password_confirmation: '',
  month_assignments: []
})

const errors = ref<Record<string, string>>({})
const isSubmitting = ref(false)
const showPassword = ref(false)
const updatePassword = ref(false)

// Store original data to track changes
const originalFormData = ref<{
  first_name: string
  last_name: string
  middle_name: string
  email: string
  phone_number: string
  city: string
  state: string
  zip_code: string
}>({
  first_name: '',
  last_name: '',
  middle_name: '',
  email: '',
  phone_number: '',
  city: '',
  state: '',
  zip_code: ''
})

// Month assignment state
const isLoadingAssignments = ref(false)
const assignmentsLoaded = ref(false)
const editingAssignmentIndex = ref<number | null>(null)
const hasMonthAccessChanges = ref(false)
const originalMonthAssignments = ref<Array<{
  assigned_year: number
  assigned_months: number[]
}>>([])

// Temporary assignment form for UI manipulation
const tempAssignmentForm = reactive<CreateAssignmentPayload>({
  assigned_year: new Date().getFullYear(),
  assigned_months: []
})

const assignmentErrors = ref<Record<string, string>>({})

// Available years (current year to 5 years ahead)
const availableYears = computed(() => {
  const currentYear = new Date().getFullYear()
  const years = []
  for (let i = 0; i <= 5; i++) {
    years.push(currentYear + i)
  }
  return years
})

// Months data
const months = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' },
  { value: 5, label: 'May' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dec' }
]

// Watch for modal visibility and employee prop changes to populate form and fetch assignments
watch(
  [() => props.isVisible, () => props.employee],
  ([isVisible, employee]) => {
    if (isVisible && employee) {
      formData.first_name = employee.first_name
      formData.last_name = employee.last_name
      formData.middle_name = employee.middle_name || ''
      formData.email = employee.email
      formData.phone_number = employee.phone_number || ''
      formData.city = employee.city || ''
      formData.state = employee.state || ''
      formData.zip_code = employee.zip_code || ''
      formData.password = ''
      formData.password_confirmation = ''
      formData.month_assignments = []

      // Store original values for change detection
      originalFormData.value = {
        first_name: employee.first_name,
        last_name: employee.last_name,
        middle_name: employee.middle_name || '',
        email: employee.email,
        phone_number: employee.phone_number || '',
        city: employee.city || '',
        state: employee.state || '',
        zip_code: employee.zip_code || ''
      }

      // Reset password-related state
      updatePassword.value = false
      errors.value = {}

      // Reset assignment temporary form & changes flag
      resetTempAssignmentForm()
      hasMonthAccessChanges.value = false
      originalMonthAssignments.value = []

      // Automatically fetch current monthly assignments
      loadCurrentAssignments()
    }
  },
  { immediate: true }
)

// Watch updatePassword to clear password fields when disabled
watch(updatePassword, newValue => {
  if (!newValue) {
    formData.password = ''
    formData.password_confirmation = ''
  }
})

// Watch for password changes to auto-update confirmation
watch(
  () => formData.password,
  newPassword => {
    if (updatePassword.value) {
      formData.password_confirmation = newPassword
    }
  }
)

// Live validation computed properties
const isPasswordTooShort = computed(() => {
  return updatePassword.value && formData.password && formData.password.length < 6
})

const isPasswordValidLength = computed(() => {
  return updatePassword.value && formData.password && formData.password.length >= 6
})

const isPasswordMismatch = computed(() => {
  return false // No password confirmation field, so never a mismatch
})

// Name validation computed properties
const isFirstNameEmpty = computed(() => {
  return !formData.first_name || formData.first_name.trim() === ''
})

const isLastNameEmpty = computed(() => {
  return !formData.last_name || formData.last_name.trim() === ''
})

// Email validation
const isEmailInvalid = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return formData.email && formData.email.trim() !== '' && !emailRegex.test(formData.email)
})

const isEmailEmpty = computed(() => {
  return !formData.email || formData.email.trim() === ''
})

const nameValidationMessage = computed(() => {
  if (isFirstNameEmpty.value && isLastNameEmpty.value) {
    return 'First Name and Last Name are required'
  } else if (isFirstNameEmpty.value) {
    return 'First name is required'
  } else if (isLastNameEmpty.value) {
    return 'Last name is required'
  }
  return ''
})

// Check if any form data has changed
const hasFormChanges = computed(() => {
  // Check if basic info has changed
  const basicInfoChanged = 
    formData.first_name !== originalFormData.value.first_name ||
    formData.last_name !== originalFormData.value.last_name ||
    formData.middle_name !== originalFormData.value.middle_name ||
    formData.email !== originalFormData.value.email ||
    formData.phone_number !== originalFormData.value.phone_number ||
    formData.city !== originalFormData.value.city ||
    formData.state !== originalFormData.value.state ||
    formData.zip_code !== originalFormData.value.zip_code

  // Check if password is being updated
  const passwordChanged = updatePassword.value && formData.password !== ''

  // Check if month assignments have changed
  const monthAssignmentsChanged = hasMonthAccessChanges.value

  return basicInfoChanged || passwordChanged || monthAssignmentsChanged
})

// Disable submit button if no changes, form is submitting, or assignments are loading
const isSubmitDisabled = computed(() => {
  return !hasFormChanges.value || isSubmitting.value || isLoadingAssignments.value
})

// Methods
const validateForm = (): boolean => {
  errors.value = {}

  // Use the same validation logic as the live validation
  if (isFirstNameEmpty.value) {
    errors.value.first_name = 'First name is required'
  }

  if (isLastNameEmpty.value) {
    errors.value.last_name = 'Last name is required'
  }

  if (!formData.email.trim()) {
    errors.value.email = 'Email is required'
  } else if (isEmailInvalid.value) {
    errors.value.email = 'Please enter a valid email address'
  }

  // Password validation only if updating password
  if (updatePassword.value) {
    if (!formData.password) {
      errors.value.password = 'Password is required'
    } else if (isPasswordTooShort.value) {
      errors.value.password = 'Password must be at least 6 characters long'
    }
  }

  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  if (!validateForm() || !props.employee) return

  isSubmitting.value = true

  try {
    // Prepare the update data
    const updateData: any = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      middle_name: formData.middle_name.trim() || null,
      email: formData.email.trim(),
      phone_number: formData.phone_number.trim() || null,
      city: formData.city.trim() || null,
      state: formData.state.trim() || null,
      zip_code: formData.zip_code.trim() || null
    }

    // Add password fields only if updating password
    if (updatePassword.value) {
      updateData.password = formData.password
      updateData.password_confirmation = formData.password_confirmation
    }

    // Add month assignments - only include if assignments have been modified
    // This allows sending an empty array to remove all assignments while preventing
    // accidental wiping when only updating details or password
    if (hasMonthAccessChanges.value && formData.month_assignments !== undefined) {
      updateData.month_assignments = formData.month_assignments
    }

    // Import the API service dynamically to avoid circular imports
    const { default: api } = await import('@/services/api')

    // Call the update endpoint
    const response = await api.put(`/users/${props.employee.user_details_id}`, updateData)

    if (response.data.success) {
      // Show success toast message with different messages based on whether password was updated
      if (updatePassword.value) {
        toast.success(
          `${formData.first_name} ${formData.last_name}'s details and password have been updated.`
        )
      } else {
        toast.success(
          `${formData.first_name} ${formData.last_name}'s details have been updated.`
        )
      }

      // Reset change tracking since changes have been saved
      hasMonthAccessChanges.value = false
      
      // Update originalFormData to reflect the saved changes
      originalFormData.value = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        middle_name: formData.middle_name,
        email: formData.email,
        phone_number: formData.phone_number,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code
      }

      // Reset password fields after successful update
      if (updatePassword.value) {
        formData.password = ''
        formData.password_confirmation = ''
        updatePassword.value = false
        showPassword.value = false
      }

      // Emit success event with updated employee data
      emit('success', response.data.data)
      closeModal()
    } else {
      throw new Error(response.data.message || 'Failed to update employee')
    }
  } catch (error: any) {
    console.error('Error updating employee:', error)

    // Handle validation errors from server
    if (error.response?.status === 422 && error.response?.data?.errors) {
      // Convert array errors to string errors
      const apiErrors = error.response.data.errors
      Object.keys(apiErrors).forEach(key => {
        errors.value[key] = Array.isArray(apiErrors[key]) ? apiErrors[key][0] : apiErrors[key]
      })
      const firstField = Object.keys(error.response.data.errors)[0]
      const firstError = error.response.data.errors[firstField][0]
      toast.error('Validation Error', firstError)
    } else {
      // Handle other errors
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to update employee'
      errors.value.general = errorMessage
      toast.error('Update Failed', errorMessage)
    }
  } finally {
    isSubmitting.value = false
  }
}

// Month assignment methods
const loadCurrentAssignments = async () => {
  const currentEmployeeId = props.employee?.user_id
  if (!currentEmployeeId) return

  isLoadingAssignments.value = true
  assignmentsLoaded.value = false
  try {
    const response = await EmployeeAssignmentService.getAllAssignments({
      employee_id: currentEmployeeId
    })

    // If modal was closed or employee changed during request, do not update state
    if (!props.isVisible || props.employee?.user_id !== currentEmployeeId) {
      return
    }

    if (response.success) {
      // Filter assignments for this specific employee
      const employeeAssignments = (response.data || []).filter(
        assignment => assignment.employee_id === currentEmployeeId
      )

      // Convert to form format with sorted months
      formData.month_assignments = employeeAssignments.map(assignment => ({
        assigned_year: assignment.assigned_year,
        assigned_months: [...assignment.assigned_months].sort((a, b) => a - b)
      }))

      // Sort assignments by year
      formData.month_assignments.sort((a, b) => a.assigned_year - b.assigned_year)

      // Store original assignments for comparison
      originalMonthAssignments.value = JSON.parse(JSON.stringify(formData.month_assignments))
      hasMonthAccessChanges.value = false
      assignmentsLoaded.value = true
    }
  } catch (error: any) {
    if (props.isVisible && props.employee?.user_id === currentEmployeeId) {
      console.error('Error loading employee assignments:', error)
      toast.error('Failed to load employee assignments')
    }
  } finally {
    if (props.employee?.user_id === currentEmployeeId) {
      isLoadingAssignments.value = false
    }
  }
}

const getMonthNames = (monthNumbers: number[]): string[] => {
  return monthNumbers.map(num => {
    const month = months.find(m => m.value === num)
    return month ? month.label : num.toString()
  })
}

const toggleMonthInTemp = (monthValue: number) => {
  const index = tempAssignmentForm.assigned_months.indexOf(monthValue)
  if (index === -1) {
    tempAssignmentForm.assigned_months.push(monthValue)
  } else {
    tempAssignmentForm.assigned_months.splice(index, 1)
  }
  // Sort months in ascending order
  tempAssignmentForm.assigned_months.sort((a, b) => a - b)
}

const validateTempAssignmentForm = (): boolean => {
  assignmentErrors.value = {}

  if (!tempAssignmentForm.assigned_year) {
    assignmentErrors.value.assigned_year = 'Year is required'
  }

  if (tempAssignmentForm.assigned_months.length === 0) {
    assignmentErrors.value.assigned_months = 'At least one month must be selected'
  }

  // Check if this year already exists in the form (and not being edited)
  if (formData.month_assignments) {
    const existingYearIndex = formData.month_assignments.findIndex(
      assignment => assignment.assigned_year === tempAssignmentForm.assigned_year
    )
    
    if (existingYearIndex !== -1 && existingYearIndex !== editingAssignmentIndex.value) {
      assignmentErrors.value.assigned_year = 'Assignment for this year already exists'
    }
  }

  return Object.keys(assignmentErrors.value).length === 0
}

const addOrUpdateAssignmentInForm = () => {
  if (!validateTempAssignmentForm()) return

  if (!formData.month_assignments) {
    formData.month_assignments = []
  }

  const newAssignment = {
    assigned_year: tempAssignmentForm.assigned_year,
    assigned_months: [...tempAssignmentForm.assigned_months].sort((a, b) => a - b)
  }

  if (editingAssignmentIndex.value !== null) {
    // Update existing assignment
    formData.month_assignments[editingAssignmentIndex.value] = newAssignment
  } else {
    // Add new assignment
    formData.month_assignments.push(newAssignment)
  }

  // Sort assignments by year
  formData.month_assignments.sort((a, b) => a.assigned_year - b.assigned_year)

  // Mark that changes have been made
  checkForMonthAccessChanges()

  resetTempAssignmentForm()
}

const editAssignmentInForm = (index: number) => {
  if (!formData.month_assignments || !formData.month_assignments[index]) return
  
  const assignment = formData.month_assignments[index]
  editingAssignmentIndex.value = index
  tempAssignmentForm.assigned_year = assignment.assigned_year
  tempAssignmentForm.assigned_months = [...assignment.assigned_months]
  assignmentErrors.value = {}
}

const removeAssignmentFromForm = (index: number) => {
  if (!formData.month_assignments) return

  formData.month_assignments.splice(index, 1)
  
  // Mark that changes have been made
  checkForMonthAccessChanges()
  
  // Reset form if we were editing this assignment
  if (editingAssignmentIndex.value === index) {
    resetTempAssignmentForm()
  } else if (editingAssignmentIndex.value !== null && editingAssignmentIndex.value > index) {
    // Adjust the editing index if it was after the removed item
    editingAssignmentIndex.value--
  }
}

const cancelEditAssignment = () => {
  resetTempAssignmentForm()
}

const resetTempAssignmentForm = () => {
  editingAssignmentIndex.value = null
  tempAssignmentForm.assigned_year = new Date().getFullYear()
  tempAssignmentForm.assigned_months = []
  assignmentErrors.value = {}
}

const checkForMonthAccessChanges = () => {
  // Deep comparison of assignments
  const current = JSON.stringify(
    (formData.month_assignments || []).map(a => ({
      assigned_year: a.assigned_year,
      assigned_months: [...a.assigned_months].sort((x, y) => x - y)
    })).sort((a, b) => a.assigned_year - b.assigned_year)
  )
  const original = JSON.stringify(
    (originalMonthAssignments.value || []).map(a => ({
      assigned_year: a.assigned_year,
      assigned_months: [...a.assigned_months].sort((x, y) => x - y)
    })).sort((a, b) => a.assigned_year - b.assigned_year)
  )
  hasMonthAccessChanges.value = current !== original
}

const closeModal = () => {
  // Check if there are any unsaved changes
  if (hasFormChanges.value) {
    const confirmClose = confirm(
      'You have unsaved changes.\n\n' +
      'Please click "Update Employee" to save these changes.\n\n' +
      'Do you want to close without saving?'
    )
    
    if (!confirmClose) {
      return // Don't close the modal
    }
  }

  // Reset form
  if (props.employee) {
    formData.first_name = props.employee.first_name
    formData.last_name = props.employee.last_name
    formData.middle_name = props.employee.middle_name || ''
    formData.email = props.employee.email
    formData.phone_number = props.employee.phone_number || ''
    formData.city = props.employee.city || ''
    formData.state = props.employee.state || ''
    formData.zip_code = props.employee.zip_code || ''
    
    // Reset original data as well
    originalFormData.value = {
      first_name: props.employee.first_name,
      last_name: props.employee.last_name,
      middle_name: props.employee.middle_name || '',
      email: props.employee.email,
      phone_number: props.employee.phone_number || '',
      city: props.employee.city || '',
      state: props.employee.state || '',
      zip_code: props.employee.zip_code || ''
    }
  }
  formData.password = ''
  formData.password_confirmation = ''

  // Reset state
  errors.value = {}
  isSubmitting.value = false
  updatePassword.value = false
  showPassword.value = false

  // Reset assignment state
  hasMonthAccessChanges.value = false
  originalMonthAssignments.value = []
  formData.month_assignments = []
  isLoadingAssignments.value = false
  assignmentsLoaded.value = false
  resetTempAssignmentForm()

  emit('close')
}
</script>
